import { CalendarDaysIcon } from '@heroicons/react/24/outline'
import { differenceInCalendarDays, format, formatDistanceToNow } from 'date-fns'
import { de, enUS as en } from 'date-fns/locale'
import PropTypes from 'prop-types'
import { forwardRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BeatLoader } from 'react-spinners'

import { colors } from '../../constants/colors'
import trackEvents from '../../constants/track-events'
import { useLanguageContext, useNotificationContext } from '../../context'
import {
  ARCHIVED_CAREER_ACTIONS,
  ARCHIVED_CAREER_HINTS,
  isSpaceOfArchivedCareer
} from '../../features/pricing/archived-careers'
import { mutation } from '../../graphql'
import { trackEvent } from '../../helpers/analytics'
import {
  pickerDateToExpiresAt,
  toPickerDate
} from '../../helpers/assessment-deadline'
import { useArchivedCareerIds } from '../../hooks/use-archived-career-ids'
import { useCreateLog } from '../../hooks/use-create-log'
import ArchivedCareerRestoreAction from '../actions/archived-career-restore-action'
import Datepicker from '../react-datepicker/datepicker'
import DetailField from '../tailwind/DetailField'
import InfoCallout from '../tailwind/InfoCallout'

const DATE_FORMAT = 'dd.MM.yyyy'

const CalendarButton = forwardRef(({ date, onClick }, ref) => (
  <button
    className='text-base font-medium text-gray-900 hover:text-primaryBlue'
    onClick={onClick}
    ref={ref}
  >
    {format(date, DATE_FORMAT)}
  </button>
))

CalendarButton.propTypes = {
  date: PropTypes.object.isRequired,
  onClick: PropTypes.func
}

CalendarButton.defaultProps = {
  onClick: () => undefined
}

// Ablauf des Assessments auf der Talent-Detailseite. Ein neues Datum ist
// dieselbe Aktion wie „Bearbeitungszeit verlängern" (RESET_EXPIRED). Bei
// archivierter Stelle (D16, jeder Modus) ist sie deshalb gesperrt, statt des
// Datepickers steht das Datum mit dem Hinweis zum Wiederherstellen da. Die
// Stelle kennt der Cache von getCareer, den die Detailseite lädt.
const SpaceExpiresAtField = ({ space, refetchSpace }) => {
  const { t } = useTranslation()
  const { language } = useLanguageContext()
  const { success } = useNotificationContext()
  const { createSpaceHistoryLog } = useCreateLog()

  const { id: space_id, career_id: space_career_id, expiresAt } = space

  const [isUpdatingExpiresAt, setIsUpdatingExpiresAt] = useState(false)

  const { archivedCareerIds } = useArchivedCareerIds({ spaces: [space] })
  const careerArchived = isSpaceOfArchivedCareer(space, archivedCareerIds)

  const updateExpiresAt = async (date) => {
    if (careerArchived) return

    setIsUpdatingExpiresAt(true)

    const event = { context: 'SYSTEM', status: 'RESET_EXPIRED' }

    try {
      await mutation({
        mutation: 'updateSpace',
        input: { id: space_id, expiresAt: pickerDateToExpiresAt(date) }
      })
      await createSpaceHistoryLog({
        space_id,
        career_id: space_career_id,
        ...event
      })

      await refetchSpace()
      success(t('space_actions.reset_expired_success'))
      trackEvent(trackEvents.RESET_EXPIRED_TALENTS)
    } catch (err) {
      console.error(err)
    } finally {
      setIsUpdatingExpiresAt(false)
    }
  }

  if (!expiresAt) return null

  const expiresDistance = formatDistanceToNow(new Date(expiresAt), {
    addSuffix: true,
    locale: { de, en }[language]
  })
  // Kalendertage in Berliner Zeit, wie Fristende und Mail
  const isExpired =
    differenceInCalendarDays(
      toPickerDate(expiresAt),
      toPickerDate(new Date().toISOString())
    ) < 0

  const renderValue = () => {
    if (isUpdatingExpiresAt) {
      return (
        <BeatLoader color={colors.darkGrey} speedMultiplier={0.8} size={10} />
      )
    }

    if (careerArchived) {
      return (
        <p className='text-base font-medium text-gray-900'>
          {format(toPickerDate(expiresAt), DATE_FORMAT)}
        </p>
      )
    }

    return (
      <Datepicker
        selected={toPickerDate(expiresAt)}
        onChange={updateExpiresAt}
        minDate={toPickerDate(new Date().toISOString())}
        customInput={<CalendarButton date={toPickerDate(expiresAt)} />}
      />
    )
  }

  return (
    <>
      <DetailField
        icon={CalendarDaysIcon}
        label={t('pages.talent.space_details_expires_at_label')}
        caption={t(
          isExpired
            ? 'pages.talent.expires_expired'
            : 'pages.talent.expires_in',
          { distance: expiresDistance }
        )}
      >
        {renderValue()}
      </DetailField>
      {careerArchived && (
        <InfoCallout
          tone='warning'
          className='sm:col-span-2'
          action={<ArchivedCareerRestoreAction />}
        >
          {t(ARCHIVED_CAREER_HINTS[ARCHIVED_CAREER_ACTIONS.RESET_EXPIRED])}
        </InfoCallout>
      )}
    </>
  )
}

SpaceExpiresAtField.propTypes = {
  space: PropTypes.object.isRequired,
  refetchSpace: PropTypes.func.isRequired
}

export default SpaceExpiresAtField
